import type { StylesConfig } from "react-select";

export type FacetSelectOption = { value: string; label: string };

/** Shown when the underlying facet value is an empty string so the row is still selectable. */
export const FACET_SELECT_EMPTY_VALUE_LABEL = "(empty)";

/** Deduped labels for react-select `options` / `value` (trimmed; empty string kept with a visible label). */
export function toFacetSelectOptions(strings: string[]): FacetSelectOption[] {
    const normalized = strings.map((v) => String(v ?? "").trim());
    return [...new Set(normalized)].map((value) => ({
        value,
        label: value === "" ? FACET_SELECT_EMPTY_VALUE_LABEL : value,
    }));
}

export function getFacetSelectStyles(
  withMultiValueStyles = false
): StylesConfig<FacetSelectOption, boolean> {
  return {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderRadius: 8,
      borderColor: state.isFocused ? "#818cf8" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(129, 140, 248, 0.2)" : "0 1px 2px rgba(0,0,0,0.05)",
      backgroundColor: "#ffffff",
      paddingLeft: 6,
      paddingRight: 6,
      fontSize: 13,
      color: "#1e293b",
      "&:hover": { borderColor: "#cbd5e1" },
    }),
    valueContainer: (base) => ({
      ...base,
      paddingTop: 3,
      paddingBottom: 3,
      gap: 4,
    }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 13 }),
    input: (base) => ({ ...base, color: "#1e293b", fontSize: 13 }),
    menu: (base) => ({
      ...base,
      borderRadius: 8,
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 20px rgba(15, 23, 42, 0.08)",
      overflow: "hidden",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      backgroundColor: state.isSelected ? "#eef2ff" : state.isFocused ? "#f8fafc" : "#fff",
      color: state.isSelected ? "#3730a3" : "#1e293b",
      cursor: "pointer",
    }),
    ...(withMultiValueStyles
      ? {
          multiValue: (base) => ({
            ...base,
            backgroundColor: "#eef2ff",
            border: "1px solid #e0e7ff",
            borderRadius: 6,
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: "#4338ca",
            fontSize: 11,
            fontWeight: 500,
          }),
          multiValueRemove: (base) => ({
            ...base,
            color: "#818cf8",
            borderRadius: 4,
            ":hover": { backgroundColor: "#e0e7ff", color: "#4f46e5" },
          }),
        }
      : {}),
  };
}
